import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import {
  AlertCircle,
  ArrowLeft,
  Camera01,
  Check,
  ChevronDown,
  Send01,
  User01,
  X,
} from "@untitledui/icons"
import { useNavigate } from "react-router"
import { toast } from "sonner"
import { ApiError } from "@/lib/api/client"
import {
  Field,
  PrimaryButton,
  onboardingInputClass,
} from "@/components/onboarding/onboarding-shell"
import { cx } from "@/lib/utils/cx"
import { formatPhone, isValidPhone } from "@/lib/utils/phone"
import { DAMAGE_ZONE_COPY, SEND_COPY } from "./copy"
import { SHOT_SEQUENCE } from "./types"
import type { CapturedShot, DamageZone, ShotKey, VinDecodedVehicle } from "./types"
import {
  readRecentCustomer,
  recordRecentCustomer,
  type ConsumerIntakeEstimateActions,
} from "./use-consumer-intake-estimate-state"
import { submitConsumerIntakeEstimate } from "./submit"

// Quick Estimate 2 — Screen 3.
// Final review and submit. Shows the decoded vehicle, the captured
// photos in a 2x2 grid (or 1x4 on mobile), an optional notes field,
// and a collapsed customer info section. Soft-submits if < 4
// photos with a visible warning.

interface StepSendProps {
  vehicle: VinDecodedVehicle | null
  shots: Partial<Record<ShotKey, CapturedShot>>
  closeUps: CapturedShot[]
  notes: string
  damageZones: DamageZone[]
  customer: { name: string; phone: string }
  actions: ConsumerIntakeEstimateActions
  teamId: number | string
  onBack: () => void
  onPhotoTap: (slotKey: ShotKey) => void
}

type SubmitState =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "success"; estimateId: number }
  | { kind: "error"; message: string }

export function StepSend({
  vehicle,
  shots,
  closeUps,
  notes,
  damageZones,
  customer,
  actions,
  teamId,
  onBack,
  onPhotoTap,
}: StepSendProps) {
  const navigate = useNavigate()
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: "idle" })
  const [customerExpanded, setCustomerExpanded] = useState(
    !!(customer.name || customer.phone),
  )
  const [phoneError, setPhoneError] = useState<string | null>(null)

  // Recent-customer pre-fill: if the user just submitted another
  // estimate (likely a return visit / next car), offer one-tap fill.
  const [recentCustomer, setRecentCustomer] = useState<{
    name: string
    phone: string
  } | null>(null)
  useEffect(() => {
    if (customer.name || customer.phone) return
    const cached = readRecentCustomer()
    if (cached && (cached.name || cached.phone)) {
      setRecentCustomer({ name: cached.name, phone: cached.phone })
    }
  }, [customer.name, customer.phone])

  const requiredCount = (Object.keys(shots) as ShotKey[]).length
  const allFour = requiredCount === 4

  const phoneOk =
    customer.phone.trim().length === 0 || isValidPhone(customer.phone.trim())

  const handleSubmit = async () => {
    if (!phoneOk) {
      setPhoneError("Enter a valid phone number")
      return
    }
    // Strong haptic on submit — the moment the user commits.
    try {
      navigator.vibrate?.([10, 30, 10])
    } catch {
      // ignore
    }
    setSubmitState({ kind: "sending" })
    try {
      // Compose final notes: tap-and-mark damage zones go into the
      // description so the body shop sees structured "Damage at X, Y"
      // alongside the user's free-text notes.
      const damageZoneText =
        damageZones.length > 0
          ? `Damage at: ${damageZones.map((z) => DAMAGE_ZONE_COPY[z]).join(", ")}`
          : ""
      const composedNotes = [damageZoneText, notes.trim()]
        .filter(Boolean)
        .join("\n\n")

      const result = await submitConsumerIntakeEstimate({
        teamId,
        vin: vehicle?.vin ?? "",
        vehicle,
        shots,
        closeUps,
        notes: composedNotes,
        customer,
      })
      // Cache the customer for the next estimate within 30 minutes.
      recordRecentCustomer(customer.name, customer.phone)
      // Clear the saved draft now that the estimate is real.
      actions.reset()
      setSubmitState({ kind: "success", estimateId: result.estimateId })
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Couldn't send the estimate"
      setSubmitState({ kind: "error", message: msg })
      toast.error(msg)
    }
  }

  // Success takeover replaces the full screen. Show the phone we'll
  // text so the user can verify it before walking away.
  if (submitState.kind === "success") {
    return (
      <SuccessTakeover
        phone={customer.phone}
        onPrimary={() => navigate(`/estimates/${submitState.estimateId}`)}
        onSecondary={() => navigate("/dashboard")}
      />
    )
  }

  return (
    <div className="space-y-6">
      <header>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-3">
          Step 3 of 3 · Review
        </p>
        <h2 className="text-2xl md:text-[28px] font-bold text-zinc-900 tracking-tight leading-tight">
          {SEND_COPY.title}
        </h2>
      </header>

      {/* Vehicle summary — color swatch only when the VIN decode
          returned a colorHex (most don't). No swatch is better than
          a fake one. */}
      <div className="rounded-2xl border border-zinc-200 bg-gradient-to-br from-zinc-50/50 to-white p-5 flex items-start gap-4">
        {vehicle?.colorHex && (
          <div
            className="size-12 rounded-xl shrink-0 shadow-sm border border-zinc-200/70"
            style={{ background: vehicle.colorHex }}
            aria-hidden
          />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-1.5">
            Vehicle
          </p>
          <p className="text-[18px] font-semibold text-zinc-900 leading-tight">
            {[vehicle?.year, vehicle?.make, vehicle?.model, vehicle?.trim]
              .filter(Boolean)
              .join(" ") || "—"}
          </p>
          {vehicle?.vin && (
            <p className="mt-1.5 text-[12px] font-mono text-zinc-500 truncate">
              {vehicle.vin}
            </p>
          )}
        </div>
      </div>

      {/* Damage zones summary — show the panels the user marked so
          they can review before submitting. */}
      {damageZones.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400">
            Damage marked
          </p>
          <div className="flex flex-wrap gap-1.5">
            {damageZones.map((z) => (
              <span
                key={z}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold bg-emerald-50 text-emerald-800"
              >
                <Check className="size-3" aria-hidden />
                {DAMAGE_ZONE_COPY[z]}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Photos grid — tap a slot to retake. */}
      <div className="space-y-3">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400">
          Photos · tap to retake
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {SHOT_SEQUENCE.map((s) => {
            const shot = shots[s.key]
            return (
              <PhotoSlotButton
                key={s.key}
                slotKey={s.key}
                slotLabel={s.label}
                shot={shot}
                onTap={() => onPhotoTap(s.key)}
              />
            )
          })}
        </div>
      </div>

      {closeUps.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400">
            Close-ups · {closeUps.length}
          </p>
          <div className="grid grid-cols-4 gap-2.5">
            {closeUps.map((shot, i) => (
              <div
                key={i}
                className="aspect-square rounded-xl overflow-hidden border border-zinc-200"
              >
                <img
                  src={shot.previewUrl}
                  alt={`Close-up ${i + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes — chip-pickers above the textarea so the user can
          tap-pick the common scenarios instead of typing. Tap a chip
          → it appends to Notes and toggles selected. The textarea
          remains for any other detail. */}
      <Field label={SEND_COPY.notesLabel}>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {SEND_COPY.damageChips.map((chip) => {
            const selected = notes.includes(chip)
            return (
              <button
                key={chip}
                type="button"
                onClick={() => {
                  if (selected) {
                    // Remove this chip and any leading separator
                    const next = notes
                      .split("\n")
                      .filter((line) => line.trim() !== chip)
                      .join("\n")
                      .trim()
                    actions.setNotes(next)
                  } else {
                    actions.setNotes(notes ? `${notes}\n${chip}` : chip)
                  }
                }}
                className={cx(
                  "px-3 py-1.5 rounded-full text-[12.5px] font-semibold transition-all duration-150",
                  selected
                    ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
                    : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200/80",
                )}
              >
                {chip}
              </button>
            )
          })}
        </div>
        <textarea
          value={notes}
          onChange={(e) => actions.setNotes(e.target.value)}
          placeholder={SEND_COPY.notesPlaceholder}
          rows={3}
          maxLength={1000}
          className={cx(
            onboardingInputClass,
            "h-auto py-3 resize-none leading-relaxed",
          )}
        />
      </Field>

      {/* Customer info — collapsed by default */}
      <div className="rounded-2xl border border-zinc-200 bg-white">
        <button
          type="button"
          onClick={() => setCustomerExpanded((e) => !e)}
          className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-zinc-50/50 transition-colors duration-150"
          aria-expanded={customerExpanded}
        >
          <div className="size-9 rounded-xl bg-zinc-100 grid place-items-center shrink-0">
            <User01 className="size-4 text-zinc-600" aria-hidden />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13.5px] font-semibold text-zinc-900 leading-tight">
              {SEND_COPY.customerToggle}
            </p>
            <p className="text-[11.5px] text-zinc-500 mt-0.5 leading-tight">
              Optional
            </p>
          </div>
          <ChevronDown
            className={cx(
              "size-4 text-zinc-400 shrink-0 transition-transform duration-200",
              customerExpanded && "rotate-180",
            )}
            aria-hidden
          />
        </button>
        <AnimatePresence initial={false}>
          {customerExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="overflow-hidden"
            >
              <div className="border-t border-zinc-100 px-4 py-4 space-y-4">
                {/* Recent customer one-tap fill — only when fields are
                    empty AND a recent submission exists. Disappears
                    after pre-fill. */}
                {recentCustomer && !customer.name && !customer.phone && (
                  <button
                    type="button"
                    onClick={() => {
                      actions.setCustomer({
                        name: recentCustomer.name,
                        phone: recentCustomer.phone,
                      })
                      setRecentCustomer(null)
                    }}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50 transition-colors duration-150 text-left"
                  >
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                        {SEND_COPY.recentCustomerPrompt}
                      </p>
                      <p className="text-[13px] font-semibold text-zinc-900 truncate mt-0.5">
                        {recentCustomer.name || "—"}{" "}
                        {recentCustomer.phone && (
                          <span className="text-zinc-500 font-normal">
                            · {formatPhone(recentCustomer.phone)}
                          </span>
                        )}
                      </p>
                      <p className="text-[10.5px] text-emerald-700/70 mt-0.5">
                        From your last estimate
                      </p>
                    </div>
                    <span className="text-[12px] font-semibold text-emerald-700">
                      Tap to use
                    </span>
                  </button>
                )}
                <Field label={SEND_COPY.customerNameLabel}>
                  <input
                    value={customer.name}
                    onChange={(e) =>
                      actions.setCustomer({ name: e.target.value })
                    }
                    placeholder="Jane Doe"
                    maxLength={120}
                    className={onboardingInputClass}
                  />
                </Field>
                <Field
                  label={SEND_COPY.customerPhoneLabel}
                  error={phoneError ?? undefined}
                >
                  <input
                    type="tel"
                    inputMode="tel"
                    value={customer.phone}
                    onChange={(e) => {
                      actions.setCustomer({ phone: formatPhone(e.target.value) })
                      if (phoneError) setPhoneError(null)
                    }}
                    placeholder="(555) 123-4567"
                    maxLength={20}
                    className={onboardingInputClass}
                  />
                </Field>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Soft-submit warning */}
      {!allFour && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 px-4 py-3 flex items-start gap-2.5">
          <AlertCircle className="size-4 text-amber-600 shrink-0 mt-0.5" aria-hidden />
          <p className="text-[13px] font-medium text-amber-900 leading-snug">
            {SEND_COPY.partialPhotosWarn}
          </p>
        </div>
      )}

      {/* Submit */}
      <div className="space-y-2 pt-2">
        <PrimaryButton
          loading={submitState.kind === "sending"}
          onClick={handleSubmit}
          type="button"
          size="wide"
        >
          <Send01 className="size-4" aria-hidden />
          {submitState.kind === "sending" ? SEND_COPY.submitting : SEND_COPY.submit}
        </PrimaryButton>
        <button
          type="button"
          onClick={onBack}
          className="w-full h-10 inline-flex items-center justify-center gap-1.5 text-[12.5px] font-medium text-zinc-500 hover:text-zinc-900 transition-colors duration-150"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          Back to photos
        </button>
      </div>
    </div>
  )
}

function SuccessTakeover({
  phone,
  onPrimary,
  onSecondary,
}: {
  phone: string
  onPrimary: () => void
  onSecondary: () => void
}) {
  const phoneTrimmed = phone.trim()
  const body = phoneTrimmed
    ? SEND_COPY.successBodyWithPhone(formatPhone(phoneTrimmed))
    : SEND_COPY.successBodyNoPhone
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 260, damping: 28 }}
      className="text-center py-12 space-y-6"
    >
      <div className="mx-auto size-20 rounded-full bg-emerald-500 grid place-items-center shadow-2xl shadow-emerald-600/30">
        <Check className="size-10 text-white" strokeWidth={3} aria-hidden />
      </div>
      <div className="space-y-2">
        <h2 className="text-[26px] md:text-[32px] font-bold text-zinc-900 tracking-tight leading-tight">
          {SEND_COPY.successTitle}
        </h2>
        <p className="text-[15px] text-zinc-500 leading-relaxed max-w-md mx-auto">
          {body}
        </p>
      </div>
      <div className="flex flex-col gap-2 max-w-sm mx-auto">
        <PrimaryButton onClick={onPrimary} type="button" size="wide">
          {SEND_COPY.successPrimary}
        </PrimaryButton>
        <button
          type="button"
          onClick={onSecondary}
          className="w-full h-12 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 text-[14px] font-semibold text-zinc-700 transition-colors duration-150"
        >
          {SEND_COPY.successSecondary}
        </button>
      </div>
    </motion.div>
  )
}

/* ── Photo slot button — tap to retake. ───────── */

function PhotoSlotButton({
  slotKey,
  slotLabel,
  shot,
  onTap,
}: {
  slotKey: ShotKey
  slotLabel: string
  shot?: CapturedShot
  onTap: () => void
}) {
  void slotKey
  return (
    <button
      type="button"
      onClick={onTap}
      className={cx(
        "relative aspect-square rounded-xl overflow-hidden border-2 transition-all duration-150",
        shot
          ? "border-emerald-200 hover:border-emerald-300"
          : "border-dashed border-zinc-300 bg-zinc-50/40 hover:border-zinc-400 hover:bg-zinc-50",
      )}
    >
      {shot ? (
        <>
          <img
            src={shot.previewUrl}
            alt={slotLabel}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <span className="absolute bottom-1 left-1 text-[9.5px] font-bold uppercase tracking-wide text-white bg-zinc-900/70 px-1.5 py-0.5 rounded">
            {slotLabel.split(" ")[0]}
          </span>
        </>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-zinc-400">
          <Camera01 className="size-5" aria-hidden />
          <span className="text-[10.5px] font-semibold uppercase tracking-wide">
            {slotLabel.split(" ")[0]}
          </span>
          <span className="text-[10px] font-medium">Add</span>
        </div>
      )}
    </button>
  )
}

// Suppress an unused-export TS warning for X — used only in some
// future variant of the chip pickers if we want a remove icon.
void X
